import type {TranslationKey} from '_metronic/i18n/t'

import type {ApiBook, BookStatus} from '../api/curriculumApi'

/** Statuses where the server is working on the book in the background. */
const RUNNING: BookStatus[] = ['uploaded', 'detecting', 'extracting', 'publishing']

/**
 * Whether the book should be polled: the server is working on it, or the background quality
 * check still adds flags during review (handoff §5.3). An interrupted job is not polled.
 */
export const isProcessing = (book: ApiBook): boolean =>
  !book.interrupted && (RUNNING.includes(book.status as BookStatus) || book.stage === 'ocr_check')

/** Whether the admin can resume the book (failed, or stopped by a server restart). */
export const canResume = (book: ApiBook): boolean => book.status === 'failed' || Boolean(book.interrupted)

/** Whether deleting is allowed (the API answers 409 while processing). */
export const canDelete = (book: ApiBook): boolean => !isProcessing(book)

/** Progress as a whole percent (0 when the total is unknown). */
export const progressPercent = (book: ApiBook): number => {
  const {done = 0, total = 0} = book.progress ?? {}
  return total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
}

export const STATUS_LABEL = {
  uploaded: 'CURRICULUM_ASSISTANT.LIBRARY.STATUS_UPLOADED',
  detecting: 'CURRICULUM_ASSISTANT.LIBRARY.STATUS_DETECTING',
  needs_structure: 'CURRICULUM_ASSISTANT.LIBRARY.STATUS_NEEDS_STRUCTURE',
  extracting: 'CURRICULUM_ASSISTANT.LIBRARY.STATUS_EXTRACTING',
  in_review: 'CURRICULUM_ASSISTANT.LIBRARY.STATUS_IN_REVIEW',
  publishing: 'CURRICULUM_ASSISTANT.LIBRARY.STATUS_PUBLISHING',
  ready: 'CURRICULUM_ASSISTANT.LIBRARY.STATUS_READY',
  failed: 'CURRICULUM_ASSISTANT.LIBRARY.STATUS_FAILED',
} as const satisfies Record<BookStatus, TranslationKey>

/** Badge tone of a status. */
export const statusTone = (book: ApiBook): 'ok' | 'warn' | 'bad' | 'run' =>
  book.status === 'ready'
    ? 'ok'
    : book.status === 'failed' || book.interrupted
      ? 'bad'
      : book.status === 'needs_structure' || book.status === 'in_review'
        ? 'warn'
        : 'run'

/** The five steps shown for a book, as in the mockup's "Loading the book". */
export const PIPELINE = [
  {key: 'uploaded', label: 'CURRICULUM_ASSISTANT.LIBRARY.STEP_UPLOADED'},
  {key: 'structure', label: 'CURRICULUM_ASSISTANT.LIBRARY.STEP_STRUCTURE'},
  {key: 'read', label: 'CURRICULUM_ASSISTANT.LIBRARY.STEP_READ'},
  {key: 'review', label: 'CURRICULUM_ASSISTANT.LIBRARY.STEP_REVIEW'},
  {key: 'live', label: 'CURRICULUM_ASSISTANT.LIBRARY.STEP_LIVE'},
] as const satisfies {key: string; label: TranslationKey}[]

const ORDER: Record<BookStatus, number> = {
  uploaded: 0,
  detecting: 0,
  needs_structure: 1,
  extracting: 2,
  in_review: 3,
  publishing: 4,
  ready: 5,
  failed: -1,
}

/** Which pipeline step the book is on (0-4), 5 when live. A failed book stays on its step. */
export const pipelineStep = (book: ApiBook): number => {
  if (book.status !== 'failed') return ORDER[book.status ?? 'uploaded']
  const stage = book.stage ?? ''
  if (stage === 'render' || stage === 'detect') return 0
  if (stage === 'extract') return 2
  if (stage === 'chunk' || stage === 'embed') return 4
  return 0
}
